import { useState } from 'react';
import get from 'lodash/get';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';

import SessionListItem from '../sessions/SessionListItem';
import BlankContent from '../common/BlankContent';
import CustomBox from '../common/CustomBox';
import MetricWidget from '../common/MetricWidget';
import ProviderTableRow from '../providers/ProviderTableRow';

import {
  DescriptionBlueIcon,
  EmailIcon,
  CalendarWhiteIcon,
  ScheduleWhiteIcon,
  ArrowRightBlueIcon
} from '../../styles/icons';
import useIsMobile from "../../hooks/useIsMobile";
import { MAX_WITH_MOBILE_MEDIA } from '../../config/getTheme';
import PatientWaysToBook from '../booking/PatientWaysToBook';
import './PatientHomeScreen.scss';

const MyProvidersCard = props => {
  const { providers } = props;

  return (
    <CustomBox className='MyProvidersCard'>
      <Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} className='MyProviders__head'>
          <Stack direction='row' alignItems='center' className='MyProviders__head__left'>
            <Stack direction='row' alignItems='center' justifyContent='center' className='MyProviders__trending-up-circle'>
              <PersonIcon sx={{ color: 'white', width: '1.21875rem', height: '1.21875rem' }} />
            </Stack>
            <Typography
              variant='h2'
              color='text.primary'
              sx={{
                lineHeight: '1.875rem',
                marginLeft: '15px',
                [MAX_WITH_MOBILE_MEDIA]: {
                  fontSize: '1.125rem',
                },
              }}
            >
              My Providers
            </Typography>
          </Stack>
        </Stack>
        <Box className='phs-provider-list'>
          {(providers || []).map(p =>
            <ProviderTableRow key={p.id} provider={p} />
          )}
          {!providers || !providers.length
            ? <BlankContent
              title='No appointments found'
              titleVariant='largeInter'
              bodyVariant='normal'
              body="It looks like you don't have any appointments scheduled."
              Icon={EventBusyIcon}
            />
            : null
          }
        </Box>
      </Stack>
    </CustomBox>
  );
}

const PatientHomeScreen = props => {
  const { sessions, providers } = props;
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(true);
  const summaryStats = [
    {
      title: 'Sessions',
      Icon: DescriptionBlueIcon,
      count: get(props, 'sessionCount', 0) || 0,
      countLabel: 'You have {count} upcoming session.',
      onClick: () => navigate('/clients/sessions'),
    },
    {
      title: 'Messages',
      Icon: EmailIcon,
      count: get(props, 'messageCount', 0) || 0,
      countLabel: 'You have {count} unread messages in your inbox',
      onClick: () => navigate('/clients/messages'),
    },
    {
      title: 'Billing',
      Icon: CreditCardIcon,
      // count: get(props, 'billCount', 0) || 0,
      // countLabel: 'Invoices',
      onClick: () => navigate('/clients/settings/payments'),
    },
  ];

  const toggleAccordion = () => {
    if (isMobile) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <Box className='PatientHomeScreen'>
      <Box>
        <Stack marginBottom='20px' className='PatientHome__head'>
          <Stack flexDirection='row' justifyContent={{ xs: 'space-between', lg: 'flex-start'}} useFlexGap flexWrap="wrap" marginBottom={{ xs: 0, sm: '22px' }}>
            {
              summaryStats.map((s, i) =>
                <MetricWidget key={'MetricWidget-' + i} {...s} />
              )
            }

            {!isMobile && <MyProvidersCard providers={providers} />}
          </Stack>

          {!isMobile &&
            <Box className='PatientHome__head__right'>
              <Stack flexDirection='row' alignItems='center'>
                <Button
                  variant='secondaryGradient'
                  nowrap
                  size={isMobile ? 'small' : 'large'}
                  onClick={() => navigate('/clients/browse-providers')}
                  endIcon={<ScheduleWhiteIcon />}
                >
                  Browse Providers
                </Button>
              </Stack>
            </Box>
          }
        </Stack>
        {/* <Box mb={2}>
          <PatientWaysToBook />
        </Box> */}
        <CustomBox className='phs-sessions'>
          <Box className='phs-sessions__inner'>
            <Stack
              className='phs-sessions__head'
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                className='phs-sessions__head__title-group'
                onClick={toggleAccordion}
              >
                <Stack direction='row' alignItems='center' className='phs-sessions__head__title-group__icon-text'>
                  <Box className='phs-sessions__head__title-group__icon-text__icon'>
                    <CalendarWhiteIcon style={{width: '1.125rem', height: '1.125rem'}}/>
                  </Box>
                  <Typography
                    variant='h2'
                    color='text.primary'
                    sx={{
                      lineHeight: '1.875rem',
                      marginLeft: '10px',
                      [MAX_WITH_MOBILE_MEDIA]: {
                        fontSize: '1.125rem',
                      },
                    }}
                  >
                    Upcoming sessions
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center' style={{ display: !isMobile ? 'none' : '' }}>
                  <Typography
                    variant='large600Tight'
                    color='text.orange'
                    sx={{
                      lineHeight: '1.875rem',
                      marginRight: '8px',
                    }}
                  >
                    ({sessions ? sessions.length : 0})
                  </Typography>
                  <Button sx={{ padding: "0", minWidth: "auto" }}
                    onClick={() => navigate('/providers/sessions?is_completed=false')}>
                    <ArrowRightBlueIcon />
                  </Button>
                </Stack>
              </Stack>

              <Stack direction='row' alignItems='center' justifyContent='flex-end' className='phs-sessions__head__schedule-group'>
                <Box className='phs-sessions__head__schedule-group__buttons' style={{ display: !isMobile ? 'none' : '' }}>
                  <Button
                    variant='secondaryGradient'
                    nowrap
                    size='small'
                    onClick={() => navigate('/clients/browse-providers')}
                    endIcon={<ScheduleWhiteIcon />}
                  >
                    Browse Providers
                  </Button>
                </Box>
              </Stack>
            </Stack>

            <Box padding='0 10px' className={`mx-table-responsive ${isOpen ? 'open' : ''}`}>
              <Box className='phs-sessions__appointment-list mx-table'>
                {(sessions || []).map(s =>
                  <SessionListItem
                    key={s.id}
                    session={s}
                    isPatient={props.isPatient}
                    isMobile={isMobile}
                    isDashboard={true}
                  />
                )}
                {!sessions || !sessions.length
                  ? <BlankContent
                    title='No appointments found'
                    body="It looks like you don't have any appointments scheduled."
                    Icon={EventBusyIcon}
                  />
                  : null
                }
              </Box>
            </Box>
          </Box>
        </CustomBox>

        {isMobile && <MyProvidersCard providers={providers} />}
      </Box>
    </Box>
  );
};

export default PatientHomeScreen;
